import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BedDouble,
  BookOpen,
  Briefcase,
  Building2,
  Compass,
  ListTree,
  MapPin,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";
import { CATEGORIES, KB_META } from "@/lib/knowledgeBaseContent";

const ICON_MAP = {
  Sparkles,
  MapPin,
  BedDouble,
  Compass,
  Building2,
  Briefcase,
  BookOpen,
};

const stripAccents = (s) =>
  (s || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/**
 * Sticky sidebar table of contents.
 * - Live-highlights the section in view via IntersectionObserver.
 * - Filters categories + sections by case/accent-insensitive query.
 */
const Sidebar = ({ activeId, onJump, query, setQuery }) => {
  const q = stripAccents(query.trim());

  const filtered = useMemo(() => {
    if (!q) return CATEGORIES;
    return CATEGORIES.map((cat) => {
      const labelMatch = stripAccents(cat.label).includes(q);
      const matchingSections = cat.sections
        .map((s) => {
          const hay = stripAccents(`${s.title} ${s.subtitle || ""}`);
          const sectionMatch = labelMatch || hay.includes(q);
          const matchingSubs = (s.subsections || []).filter((sub) =>
            stripAccents(sub.title).includes(q)
          );
          if (sectionMatch) return s; // keep with all subsections
          if (matchingSubs.length) return { ...s, subsections: matchingSubs };
          return null;
        })
        .filter(Boolean);
      if (!matchingSections.length) return null;
      return { ...cat, sections: matchingSections };
    }).filter(Boolean);
  }, [q]);

  return (
    <aside
      className="kb-sidebar"
      aria-label="Knowledge base table of contents"
      data-testid="kb-sidebar"
    >
      <div className="kb-search">
        <Search className="w-4 h-4 text-stone-500" aria-hidden="true" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar en la base de conocimiento…"
          className="kb-search-input"
          data-testid="kb-sidebar-search"
        />
        {query ? (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="kb-search-clear"
            aria-label="Limpiar búsqueda"
            data-testid="kb-sidebar-search-clear"
          >
            <X className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <nav className="kb-toc">
        {filtered.length === 0 ? (
          <p className="kb-toc-empty">Sin resultados para &laquo;{query}&raquo;.</p>
        ) : (
          filtered.map((cat) => {
            const Icon = ICON_MAP[cat.icon] || ListTree;
            return (
              <div key={cat.id} className="kb-toc-cat">
                <button
                  type="button"
                  className="kb-toc-cat-label"
                  onClick={() => onJump(cat.sections[0].id)}
                  data-testid={`kb-toc-cat-${cat.id}`}
                >
                  <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                  <span>{cat.label}</span>
                </button>
                <ul className="kb-toc-list">
                  {cat.sections.map((s) => {
                    const isActive = s.id === activeId;
                    return (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => onJump(s.id)}
                          className={`kb-toc-link ${isActive ? "is-active" : ""}`}
                          aria-current={isActive ? "true" : undefined}
                          data-testid={`kb-toc-link-${s.id}`}
                        >
                          {s.title}
                        </button>
                        {s.subsections?.length ? (
                          <ul className="kb-toc-sublist">
                            {s.subsections.map((sub) => {
                              const subActive = sub.id === activeId;
                              return (
                                <li key={sub.id}>
                                  <button
                                    type="button"
                                    onClick={() => onJump(sub.id)}
                                    className={`kb-toc-sublink ${subActive ? "is-active" : ""}`}
                                    aria-current={subActive ? "true" : undefined}
                                    data-testid={`kb-toc-link-${sub.id}`}
                                  >
                                    {sub.title}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        )}
      </nav>
    </aside>
  );
};

const SubsectionBlock = ({ subsection }) => (
  <section
    id={subsection.id}
    className="kb-subsection"
    data-testid={`kb-section-${subsection.id}`}
  >
    <h3 className="kb-subsection-title">{subsection.title}</h3>
    {subsection.paragraphs?.map((p, i) => (
      <p key={`${subsection.id}-p-${i}`} className="kb-section-p">
        {p}
      </p>
    ))}
    {subsection.list ? (
      <ul className="kb-section-list">
        {subsection.list.map((item, i) => (
          <li key={`${subsection.id}-li-${i}`}>
            {item.name ? <strong>{item.name}</strong> : null}
            {item.name ? " — " : ""}
            <span>{item.body}</span>
          </li>
        ))}
      </ul>
    ) : null}
    {subsection.raw?.map((block, i) => (
      <pre key={`${subsection.id}-raw-${i}`} className="kb-section-raw">
        {block}
      </pre>
    ))}
  </section>
);

const SectionBlock = ({ section, categoryLabel }) => (
  <article
    id={section.id}
    className="kb-section"
    data-testid={`kb-section-${section.id}`}
  >
    <div className="kb-section-kicker">
      <span className="kb-section-cat">{categoryLabel}</span>
      {section.subtitle ? (
        <span className="kb-section-subtitle">{section.subtitle}</span>
      ) : null}
    </div>
    <h2 className="kb-section-title">{section.title}</h2>
    {section.paragraphs?.map((p, i) => (
      <p key={`${section.id}-p-${i}`} className="kb-section-p">
        {p}
      </p>
    ))}
    {section.list ? (
      <ul className="kb-section-list">
        {section.list.map((item, i) => (
          <li key={`${section.id}-li-${i}`}>
            {item.name ? <strong>{item.name}</strong> : null}
            {item.name ? " — " : ""}
            <span>{item.body}</span>
          </li>
        ))}
      </ul>
    ) : null}
    {section.raw?.map((block, i) => (
      <pre key={`${section.id}-raw-${i}`} className="kb-section-raw">
        {block}
      </pre>
    ))}
    {section.subsections?.length ? (
      <div className="kb-subsections">
        {section.subsections.map((sub) => (
          <SubsectionBlock key={sub.id} subsection={sub} />
        ))}
      </div>
    ) : null}
  </article>
);

const KnowledgeBasePage = () => {
  const allSections = useMemo(
    () =>
      CATEGORIES.flatMap((c) =>
        c.sections.flatMap((s) => [
          { ...s, catLabel: c.label },
          ...((s.subsections || []).map((sub) => ({ ...sub, catLabel: c.label }))),
        ])
      ),
    []
  );
  const [activeId, setActiveId] = useState(allSections[0]?.id || "");
  const [query, setQuery] = useState("");
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const articleRef = useRef(null);

  // Track which section is currently in view to highlight in the TOC.
  useEffect(() => {
    if (!articleRef.current) return undefined;
    const targets = articleRef.current.querySelectorAll("[id]");
    const obs = new IntersectionObserver(
      (entries) => {
        // Pick the topmost visible section.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -65% 0px", threshold: [0, 0.5, 1] }
    );
    targets.forEach((t) => obs.observe(t));
    return () => obs.disconnect();
  }, []);

  // Open initial hash if present in URL.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = window.location.hash.replace("#", "");
    if (hash && document.getElementById(hash)) {
      requestAnimationFrame(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, []);

  const jumpTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#${id}`);
    }
    setMobileTocOpen(false);
  };

  return (
    <div className="kb-page" data-testid="knowledgebase-page">
      <BrandMonogramEdge align="left" />

      <header className="kb-hero">
        <div className="kb-hero-inner">
          <span className="kb-hero-kicker">{KB_META.tagline}</span>
          <h1 className="kb-hero-title">{KB_META.title}</h1>
          <p className="kb-hero-intro">{KB_META.intro}</p>
          <div className="kb-hero-meta">
            <span>Última actualización · {KB_META.lastUpdated}</span>
            <span>·</span>
            <span>
              {CATEGORIES.length} categorías · {allSections.length} secciones
            </span>
          </div>
        </div>
      </header>

      <div className="kb-shell">
        {/* Mobile TOC toggle */}
        <button
          type="button"
          className="kb-toc-mobile-toggle"
          onClick={() => setMobileTocOpen((v) => !v)}
          aria-expanded={mobileTocOpen}
          data-testid="kb-toc-mobile-toggle"
        >
          <ListTree className="w-4 h-4" aria-hidden="true" />
          <span>{mobileTocOpen ? "Ocultar índice" : "Mostrar índice"}</span>
        </button>

        <div className={`kb-grid ${mobileTocOpen ? "is-mobile-toc-open" : ""}`}>
          <Sidebar activeId={activeId} onJump={jumpTo} query={query} setQuery={setQuery} />

          <main ref={articleRef} className="kb-content" data-testid="kb-content">
            {CATEGORIES.map((cat) => (
              <section
                key={cat.id}
                id={`cat-${cat.id}`}
                className="kb-category"
                data-testid={`kb-category-${cat.id}`}
              >
                <header className="kb-category-header">
                  <span className="kb-category-rule" aria-hidden="true" />
                  <span className="kb-category-label">{cat.label}</span>
                </header>
                {cat.sections.map((s) => (
                  <SectionBlock key={s.id} section={s} categoryLabel={cat.label} />
                ))}
              </section>
            ))}
          </main>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBasePage;
